import React, { useState, useEffect, useCallback } from 'react';
import { Save, DollarSign, Clock, Tag, RefreshCw, Building2, Plus, X, Bell, Mail, Phone, CreditCard, Eye, EyeOff, ExternalLink, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { RichTextEditor } from './RichTextEditor';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from '../hooks/use-toast';
import RentalCatalogue from './RentalCatalogue';
import ConfigHealthCard from './ConfigHealthCard';

const Settings = () => {
  const { facility, refreshFacility, user } = useAuth();
  const [localFacility, setLocalFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifEmails, setNotifEmails] = useState([]);
  const [notifPhones, setNotifPhones] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);

  // Razorpay integration state
  const [rzpEnabled, setRzpEnabled] = useState(false);
  const [rzpKeyId, setRzpKeyId] = useState('');
  const [rzpKeySecret, setRzpKeySecret] = useState('');
  const [rzpWebhookSecret, setRzpWebhookSecret] = useState('');
  const [showRzpSecret, setShowRzpSecret] = useState(false);
  const [showRzpWebhookSecret, setShowRzpWebhookSecret] = useState(false);
  const [rzpSaving, setRzpSaving] = useState(false);
  const [rzpHasSecret, setRzpHasSecret] = useState(false); // server already has a secret stored
  const [rzpHasWebhookSecret, setRzpHasWebhookSecret] = useState(false);
  
  // Initialize local state from facility
  const loadRazorpaySettings = useCallback(async (facilityId) => {
    try {
      const cfg = await api.request(`/facilities/${facilityId}/integrations/razorpay`);
      setRzpEnabled(!!cfg.enabled);
      setRzpKeyId(cfg.key_id || '');
      setRzpHasSecret(!!cfg.has_key_secret);
      setRzpHasWebhookSecret(!!cfg.has_webhook_secret);
      setRzpKeySecret('');
      setRzpWebhookSecret('');
    } catch {
      // No config yet — leave empty
      setRzpEnabled(false);
      setRzpKeyId('');
      setRzpHasSecret(false);
      setRzpHasWebhookSecret(false);
    }
  }, []);

  useEffect(() => {
    if (facility) {
      setLocalFacility({...facility});
      setLoading(false);
      loadNotificationSettings(facility.id);
      loadRazorpaySettings(facility.id);
    } else if (user && user.role === 'superadmin') {
      // Superadmin has no single facility — stop the loading gate so the
      // cross-facility Config Health panel can render.
      setLoading(false);
    }
  }, [facility, user, loadRazorpaySettings]);

  const loadNotificationSettings = async (facilityId) => {
    setNotifLoading(true);
    try {
      const data = await api.request(`/facilities/${facilityId}/notifications`);
      setNotifEmails(data.emails || []);
      setNotifPhones(data.phones || []);
    } catch (err) {
      console.error('Failed to load notification settings:', err);
      toast({
        title: 'Could not load notification settings',
        description: err.message || 'Showing defaults. Please refresh to retry.',
        variant: 'destructive',
      });
    } finally {
      setNotifLoading(false);
    }
  };

  const saveRazorpaySettings = async () => {
    // Basic validation
    if (rzpEnabled && !rzpKeyId.trim()) {
      toast({ title: 'Missing Key ID', description: 'Razorpay Key ID is required when enabling.', variant: 'destructive' });
      return;
    }
    if (rzpEnabled && !rzpHasSecret && !rzpKeySecret.trim()) {
      toast({ title: 'Missing Key Secret', description: 'Razorpay Key Secret is required.', variant: 'destructive' });
      return;
    }
    setRzpSaving(true);
    try {
      const payload = {
        enabled: rzpEnabled,
        key_id: rzpKeyId.trim(),
      };
      // Only send secrets if user actually typed them. Backend preserves
      // existing secrets when fields are omitted (we set them via $set on
      // submitted keys only, so missing keys keep their stored value).
      if (rzpKeySecret.trim()) payload.key_secret = rzpKeySecret.trim();
      if (rzpWebhookSecret.trim()) payload.webhook_secret = rzpWebhookSecret.trim();

      await api.request(`/facilities/${localFacility.id}/integrations/razorpay`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      toast({ title: 'Saved', description: 'Razorpay integration updated. Live keys take effect immediately.' });
      // Refresh from server
      loadRazorpaySettings(localFacility.id);
      if (typeof refreshFacility === 'function') refreshFacility();
      setRzpKeySecret('');
      setRzpWebhookSecret('');
      setRzpHasSecret(true);
      if (payload.webhook_secret) setRzpHasWebhookSecret(true);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setRzpSaving(false);
    }
  };

  const saveNotificationSettings = async () => {
    setNotifSaving(true);
    try {
      await api.request(`/facilities/${localFacility.id}/notifications`, {
        method: 'PUT',
        body: JSON.stringify({ emails: notifEmails, phones: notifPhones }),
      });
      toast({ title: "Saved", description: "Notification settings updated." });
    } catch (err) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setNotifSaving(false);
    }
  };

  const addEmail = () => {
    const trimmed = newEmail.trim();
    if (trimmed && !notifEmails.includes(trimmed) && trimmed.includes('@')) {
      setNotifEmails([...notifEmails, trimmed]);
      setNewEmail('');
    }
  };

  const addPhone = () => {
    const trimmed = newPhone.trim().replace(/\D/g, '');
    if (trimmed.length >= 10 && !notifPhones.includes(trimmed)) {
      setNotifPhones([...notifPhones, trimmed]);
      setNewPhone('');
    }
  };

  const handlePricingChange = (courtType, dayType, peakType, value) => {
    setLocalFacility(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [courtType]: {
          ...prev.pricing[courtType],
          [dayType]: {
            ...prev.pricing[courtType]?.[dayType],
            [peakType]: parseInt(value) || 0
          }
        }
      }
    }));
  };

  const handleOperatingHoursChange = (field, value) => {
    setLocalFacility(prev => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [field]: value
      }
    }));
  };

  const handlePeakHoursChange = (dayType, index, field, value) => {
    setLocalFacility(prev => {
      const newPeakHours = {...prev.peak_hours};
      if (!newPeakHours[dayType]) newPeakHours[dayType] = [];
      newPeakHours[dayType] = [...newPeakHours[dayType]];
      newPeakHours[dayType][index] = {
        ...newPeakHours[dayType][index],
        [field]: value
      };
      return {...prev, peak_hours: newPeakHours};
    });
  };

  const addPeakHourRange = (dayType) => {
    setLocalFacility(prev => {
      const newPeakHours = {...prev.peak_hours};
      if (!newPeakHours[dayType]) newPeakHours[dayType] = [];
      newPeakHours[dayType] = [...newPeakHours[dayType], {start: '06:00', end: '09:00'}];
      return {...prev, peak_hours: newPeakHours};
    });
  };

  const removePeakHourRange = (dayType, index) => {
    setLocalFacility(prev => {
      const newPeakHours = {...prev.peak_hours};
      newPeakHours[dayType] = newPeakHours[dayType].filter((_, i) => i !== index);
      return {...prev, peak_hours: newPeakHours};
    });
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.updateFacility(localFacility.id, {
        pricing: localFacility.pricing,
        operating_hours: localFacility.operating_hours,
        peak_hours: localFacility.peak_hours,
        partial_payment: localFacility.partial_payment || { enabled: false, customer_percentage: 25, min_admin_percentage: 10 },
        terms_conditions: localFacility.terms_conditions || '',
        cancellation_policy: localFacility.cancellation_policy || '',
        reschedule_policy: localFacility.reschedule_policy || ''
      });
      await refreshFacility();
      toast({
        title: "Settings Saved",
        description: "Facility settings have been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to save settings.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2">Loading settings...</span>
      </div>
    );
  }

  // Superadmin (no single facility) — show the cross-facility Config Health view.
  if (!localFacility) {
    return (
      <div className="p-6 pt-14 lg:pt-6 pb-32 lg:pb-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-gray-500">Configuration health across all facilities</p>
        </div>
        <ConfigHealthCard />
      </div>
    );
  }

  // Get unique court types from facility
  const getCourtTypes = () => {
    if (!localFacility?.courts) return [];
    const types = new Set();
    localFacility.courts.forEach(court => {
      types.add(court.court_type || court.sport);
    });
    return Array.from(types);
  };

  const courtTypes = getCourtTypes();
  const isFootball = localFacility.sports?.includes('football');
  const isPadelPickle = localFacility.sports?.includes('padel') || localFacility.sports?.includes('pickle');

  // Get display name for court type
  const getCourtTypeName = (type) => {
    const names = {
      'full': 'Full Ground (11v11)',
      'half': 'Half Ground (9v9)',
      'quarter': 'Quarter Ground (7v7)',
      '5aside': '5-Aside Field',
      'padel': 'Padel Courts',
      'pickle': 'Pickle Courts'
    };
    return names[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <div className="p-6 pt-14 lg:pt-6 pb-32 lg:pb-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-gray-500">Manage facility settings and pricing</p>
        </div>
        <div className="flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-lg">
          <Building2 className="h-5 w-5 text-gray-600" />
          <span className="font-semibold">{localFacility.name}</span>
        </div>
      </div>

      {/* Config Health (guardrail for new-venue setup) */}
      <ConfigHealthCard />

      {/* Operating Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="mr-2 h-5 w-5" />
            Operating Hours
          </CardTitle>
          <CardDescription>Set the opening and closing times for the facility</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="opStart">Opening Time</Label>
              <Input
                id="opStart"
                type="time"
                value={localFacility.operating_hours?.start || '06:00'}
                onChange={(e) => handleOperatingHoursChange('start', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="opEnd">Closing Time</Label>
              <Input
                id="opEnd"
                type="time"
                value={localFacility.operating_hours?.end || '23:00'}
                onChange={(e) => handleOperatingHoursChange('end', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Partial Payment Configuration */}
      <Card data-testid="partial-payment-card">
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="mr-2 h-5 w-5" />
            Partial Payment
          </CardTitle>
          <CardDescription>Let customers confirm a booking by paying part of the amount online; the balance is collected at the venue.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4"
              data-testid="partial-enabled-toggle"
              checked={!!localFacility.partial_payment?.enabled}
              onChange={(e) => setLocalFacility(prev => ({
                ...prev,
                partial_payment: {
                  enabled: e.target.checked,
                  customer_percentage: prev.partial_payment?.customer_percentage ?? 25,
                  min_admin_percentage: prev.partial_payment?.min_admin_percentage ?? 10,
                }
              }))}
            />
            <span className="font-medium">Enable partial payment for this venue</span>
          </label>

          {localFacility.partial_payment?.enabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Customer pays (%)</label>
                <Input
                  type="number" min={1} max={99}
                  data-testid="partial-customer-percentage"
                  value={localFacility.partial_payment?.customer_percentage ?? 25}
                  onChange={(e) => setLocalFacility(prev => ({
                    ...prev,
                    partial_payment: { ...prev.partial_payment, customer_percentage: parseInt(e.target.value) || 0 }
                  }))}
                />
                <p className="text-xs text-gray-500 mt-1">Shown to customers as the deposit to confirm (1–99).</p>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Admin minimum (%)</label>
                <Input
                  type="number" min={1} max={99}
                  data-testid="partial-admin-min-percentage"
                  value={localFacility.partial_payment?.min_admin_percentage ?? 10}
                  onChange={(e) => setLocalFacility(prev => ({
                    ...prev,
                    partial_payment: { ...prev.partial_payment, min_admin_percentage: parseInt(e.target.value) || 0 }
                  }))}
                />
                <p className="text-xs text-gray-500 mt-1">Floor for the % an admin can charge on a calendar booking.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Peak Hours Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="mr-2 h-5 w-5" />
            Peak Hours Configuration
          </CardTitle>
          <CardDescription>Define multiple time ranges for peak pricing (weekdays and weekends separately)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Weekday Peak Hours */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg">Weekday Peak Hours</h3>
              <Button variant="outline" size="sm" onClick={() => addPeakHourRange('weekday')}>
                <Plus className="h-4 w-4 mr-1" /> Add Range
              </Button>
            </div>
            <div className="space-y-2">
              {(localFacility.peak_hours?.weekday || []).map((range, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <Input
                    type="time"
                    value={range.start}
                    onChange={(e) => handlePeakHoursChange('weekday', idx, 'start', e.target.value)}
                    className="w-32"
                  />
                  <span className="text-gray-500">to</span>
                  <Input
                    type="time"
                    value={range.end}
                    onChange={(e) => handlePeakHoursChange('weekday', idx, 'end', e.target.value)}
                    className="w-32"
                  />
                  <Button variant="ghost" size="sm" onClick={() => removePeakHourRange('weekday', idx)}>
                    <X className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              {(!localFacility.peak_hours?.weekday || localFacility.peak_hours.weekday.length === 0) && (
                <p className="text-sm text-gray-500 italic">No peak hours configured for weekdays</p>
              )}
            </div>
          </div>

          {/* Weekend Peak Hours */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg">Weekend Peak Hours</h3>
              <Button variant="outline" size="sm" onClick={() => addPeakHourRange('weekend')}>
                <Plus className="h-4 w-4 mr-1" /> Add Range
              </Button>
            </div>
            <div className="space-y-2">
              {(localFacility.peak_hours?.weekend || []).map((range, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <Input
                    type="time"
                    value={range.start}
                    onChange={(e) => handlePeakHoursChange('weekend', idx, 'start', e.target.value)}
                    className="w-32"
                  />
                  <span className="text-gray-500">to</span>
                  <Input
                    type="time"
                    value={range.end}
                    onChange={(e) => handlePeakHoursChange('weekend', idx, 'end', e.target.value)}
                    className="w-32"
                  />
                  <Button variant="ghost" size="sm" onClick={() => removePeakHourRange('weekend', idx)}>
                    <X className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              {(!localFacility.peak_hours?.weekend || localFacility.peak_hours.weekend.length === 0) && (
                <p className="text-sm text-gray-500 italic">No peak hours configured for weekends</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <DollarSign className="mr-2 h-5 w-5" />
            Pricing Management
          </CardTitle>
          <CardDescription>Set peak and off-peak prices for different court types (weekday and weekend)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {courtTypes.map(courtType => (
            <div key={courtType} className="space-y-4 pb-4 border-b last:border-b-0">
              <h3 className="font-semibold text-lg">{getCourtTypeName(courtType)}</h3>
              
              {/* Weekday Pricing */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-3">Weekday Pricing (₹/hour)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Peak Hours</Label>
                    <Input
                      type="number"
                      value={localFacility.pricing?.[courtType]?.weekday?.peak || 0}
                      onChange={(e) => handlePricingChange(courtType, 'weekday', 'peak', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Off-Peak Hours</Label>
                    <Input
                      type="number"
                      value={localFacility.pricing?.[courtType]?.weekday?.off_peak || 0}
                      onChange={(e) => handlePricingChange(courtType, 'weekday', 'off_peak', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Weekend Pricing */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-medium text-orange-800 mb-3">Weekend Pricing (₹/hour)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Peak Hours</Label>
                    <Input
                      type="number"
                      value={localFacility.pricing?.[courtType]?.weekend?.peak || 0}
                      onChange={(e) => handlePricingChange(courtType, 'weekend', 'peak', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Off-Peak Hours</Label>
                    <Input
                      type="number"
                      value={localFacility.pricing?.[courtType]?.weekend?.off_peak || 0}
                      onChange={(e) => handlePricingChange(courtType, 'weekend', 'off_peak', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Booking Policies (shown to customers on the booking summary page) */}
      <Card data-testid="policies-settings">
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="mr-2 h-5 w-5" />
            Booking Policies
          </CardTitle>
          <CardDescription>
            Rich text (bold, bullets) shown to customers on the booking summary. Leave blank to use a sensible default.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label className="mb-1.5 block">Terms &amp; Conditions</Label>
            <RichTextEditor
              testId="terms-editor"
              value={localFacility.terms_conditions || ''}
              placeholder="Add your venue's terms & conditions…"
              onChange={(html) => setLocalFacility(prev => ({ ...prev, terms_conditions: html }))}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">Cancellation Policy</Label>
            <RichTextEditor
              testId="cancellation-editor"
              value={localFacility.cancellation_policy || ''}
              placeholder="Add your cancellation policy…"
              onChange={(html) => setLocalFacility(prev => ({ ...prev, cancellation_policy: html }))}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">Reschedule Policy</Label>
            <RichTextEditor
              testId="reschedule-editor"
              value={localFacility.reschedule_policy || ''}
              placeholder="Add your reschedule policy…"
              onChange={(html) => setLocalFacility(prev => ({ ...prev, reschedule_policy: html }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bell className="mr-2 h-5 w-5" />
            Notification Settings
          </CardTitle>
          <CardDescription>Manage emails and phones that receive booking and payment notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Notification Emails */}
          <div>
            <Label className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4" /> Notification Emails
            </Label>
            <div className="flex gap-2 mb-2">
              <Input
                type="email"
                placeholder="Add email address"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addEmail()}
                data-testid="notif-email-input"
              />
              <Button onClick={addEmail} size="sm" variant="outline" data-testid="notif-add-email">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {notifEmails.map((email, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                  {email}
                  <button onClick={() => setNotifEmails(notifEmails.filter((_, j) => j !== i))} className="hover:text-red-500" data-testid={`notif-remove-email-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {notifEmails.length === 0 && <p className="text-xs text-gray-400">No emails added yet</p>}
            </div>
          </div>

          {/* Notification Phones */}
          <div>
            <Label className="flex items-center gap-2 mb-2">
              <Phone className="h-4 w-4" /> Notification Phones
            </Label>
            <div className="flex gap-2 mb-2">
              <Input
                type="tel"
                placeholder="Add phone number"
                value={newPhone}
                onChange={e => setNewPhone(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addPhone()}
                data-testid="notif-phone-input"
              />
              <Button onClick={addPhone} size="sm" variant="outline" data-testid="notif-add-phone">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {notifPhones.map((phone, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                  {phone}
                  <button onClick={() => setNotifPhones(notifPhones.filter((_, j) => j !== i))} className="hover:text-red-500" data-testid={`notif-remove-phone-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {notifPhones.length === 0 && <p className="text-xs text-gray-400">No phones added yet</p>}
            </div>
          </div>

          <Button onClick={saveNotificationSettings} disabled={notifSaving} variant="outline" className="w-full" data-testid="save-notif-settings">
            {notifSaving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {notifSaving ? 'Saving...' : 'Save Notification Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* Razorpay Integration */}
      <Card data-testid="razorpay-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <CreditCard className="mr-2 h-5 w-5 text-blue-600" />
              Razorpay Integration
            </span>
            {rzpEnabled && rzpKeyId && rzpHasSecret ? (
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1" data-testid="razorpay-status-active">
                <CheckCircle className="h-3 w-3" /> Active
              </span>
            ) : (
              <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1" data-testid="razorpay-status-inactive">
                <AlertCircle className="h-3 w-3" /> Not configured
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Payments at this venue (online checkout, payment links, dynamic QR) will be routed through this Razorpay account.{' '}
            <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
              Get your keys <ExternalLink className="h-3 w-3" />
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Enable toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-sm">Enable Razorpay</p>
              <p className="text-xs text-gray-500">Required for online payments at this venue</p>
            </div>
            <button
              type="button"
              onClick={() => setRzpEnabled(v => !v)}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${rzpEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}
              data-testid="razorpay-enable-toggle"
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${rzpEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {/* Key ID */}
          <div>
            <Label htmlFor="razorpay-key-id" className="text-sm">
              Key ID <span className="text-red-500">*</span>
            </Label>
            <Input
              id="razorpay-key-id"
              type="text"
              value={rzpKeyId}
              onChange={(e) => setRzpKeyId(e.target.value)}
              placeholder="rzp_live_xxxxxxxxxxxxxx or rzp_test_xxxxxxxxxxxxxx"
              autoComplete="off"
              spellCheck={false}
              data-testid="razorpay-key-id-input"
              className="mt-1 font-mono text-xs"
            />
            <p className="text-xs text-gray-500 mt-1">Public identifier shown on the Razorpay dashboard.</p>
          </div>

          {/* Key Secret */}
          <div>
            <Label htmlFor="razorpay-key-secret" className="text-sm">
              Key Secret {!rzpHasSecret && <span className="text-red-500">*</span>}
            </Label>
            <div className="relative mt-1">
              <Input
                id="razorpay-key-secret"
                type={showRzpSecret ? 'text' : 'password'}
                value={rzpKeySecret}
                onChange={(e) => setRzpKeySecret(e.target.value)}
                placeholder={rzpHasSecret ? '•••••••••••••••• (already saved — type to replace)' : 'Paste your Razorpay Key Secret'}
                autoComplete="new-password"
                spellCheck={false}
                data-testid="razorpay-key-secret-input"
                className="font-mono text-xs pr-10"
              />
              <button
                type="button"
                onClick={() => setShowRzpSecret(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                data-testid="razorpay-show-secret"
              >
                {showRzpSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {rzpHasSecret && !rzpKeySecret && (
              <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Secret already stored. Leave blank to keep it unchanged.
              </p>
            )}
          </div>

          {/* Webhook Secret */}
          <div>
            <Label htmlFor="razorpay-webhook-secret" className="text-sm">Webhook Secret</Label>
            <div className="relative mt-1">
              <Input
                id="razorpay-webhook-secret"
                type={showRzpWebhookSecret ? 'text' : 'password'}
                value={rzpWebhookSecret}
                onChange={(e) => setRzpWebhookSecret(e.target.value)}
                placeholder={rzpHasWebhookSecret ? '•••••••••••••••• (already saved — type to replace)' : 'Optional but recommended for signature verification'}
                autoComplete="new-password"
                spellCheck={false}
                data-testid="razorpay-webhook-secret-input"
                className="font-mono text-xs pr-10"
              />
              <button
                type="button"
                onClick={() => setShowRzpWebhookSecret(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                data-testid="razorpay-show-webhook-secret"
              >
                {showRzpWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Configure your webhook on Razorpay → Settings → Webhooks. URL: <code className="bg-gray-100 px-1 rounded">{`${window.location.origin}/api/payments/webhook`}</code>
            </p>
            {rzpHasWebhookSecret && !rzpWebhookSecret && (
              <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Webhook secret already stored.
              </p>
            )}
          </div>

          {/* Helper events list */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-900">
            <p className="font-semibold mb-1.5">Required webhook events</p>
            <ul className="space-y-0.5 font-mono">
              <li>• payment.captured</li>
              <li>• payment.failed</li>
              <li>• payment_link.paid</li>
              <li>• qr_code.credited</li>
              <li>• qr_code.closed</li>
              <li>• refund.processed</li>
              <li>• refund.failed</li>
            </ul>
          </div>

          <Button onClick={saveRazorpaySettings} disabled={rzpSaving} className="w-full" data-testid="save-razorpay-settings">
            {rzpSaving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {rzpSaving ? 'Saving...' : 'Save Razorpay Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* Courts List (Read-only for now) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Tag className="mr-2 h-5 w-5" />
            Courts / Fields
          </CardTitle>
          <CardDescription>List of courts configured for this facility</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {localFacility.courts?.map((court, idx) => (
              <div key={court.id || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-semibold">{court.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{court.sport} • {court.court_type}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  court.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                }`}>
                  {court.status}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Rental / add-on catalogue (per-venue) */}
      {localFacility?.id && <RentalCatalogue facilityId={localFacility.id} />}

      {/* Save Button */}
      <Button onClick={saveSettings} className="w-full" size="lg" disabled={saving} data-testid="save-all-settings">
        <Save className="mr-2 h-5 w-5" />
        {saving ? 'Saving...' : 'Save All Settings'}
      </Button>
    </div>
  );
};

export default Settings;
